import { atom, type Getter, useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { layerAtomFamily, layerTreeAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Layer } from '../data/v1'
import { findParentId } from '../use-layer-tree-actions'

export type SizeSetByParent = { width: boolean; height: boolean }

const NOT_SET: SizeSetByParent = { width: false, height: false }
const WIDTH_SET: SizeSetByParent = { width: true, height: false }
const HEIGHT_SET: SizeSetByParent = { width: false, height: true }

function getParent(get: Getter, layerId: string): Layer | null {
	const parentId = findParentId(get(layerTreeAtom), layerId)
	return parentId ? get(layerAtomFamily(parentId)) ?? null : null
}

export function getSizeSetByParent(get: Getter, layerId: string): SizeSetByParent {
	const parent = getParent(get, layerId)
	if (parent?.type !== 'substack' || parent.childSizing !== 'equal') { return NOT_SET }
	return parent.direction === 'row' ? WIDTH_SET : HEIGHT_SET
}

function isStretchedByParent(get: Getter, layerId: string): boolean {
	const parent = getParent(get, layerId)
	if (parent?.type === 'content') { return parent.xPin === 'stretch' }
	return parent?.type === 'substack' && parent.direction === 'column' && parent.alignItems === 'stretch'
}

/**
 * Which of the layer's own dimensions an equal-split parent substack overrides. The layer's
 * stored value is kept, and applies again once the layer leaves that parent.
 */
export function useSizeSetByParent(layerId: string): SizeSetByParent {
	const store = useCanvasStore()
	const sizeAtom = useMemo(() => atom((get) => getSizeSetByParent(get, layerId)), [layerId])
	return useAtomValue(sizeAtom, { store })
}

/**
 * True when the layer sits in a row that is only as wide as its content, so `widthSizing:
 * 'fill'` has no spare width to grow into.
 */
export function getHasNoWidthToFill(get: Getter, layerId: string): boolean {
	const parent = getParent(get, layerId)
	return parent?.type === 'substack'
		&& parent.direction === 'row'
		&& parent.widthSizing === 'auto'
		&& !getSizeSetByParent(get, parent.id).width
		&& !isStretchedByParent(get, parent.id)
}

export function useHasNoWidthToFill(layerId: string): boolean {
	const store = useCanvasStore()
	const noRoomAtom = useMemo(() => atom((get) => getHasNoWidthToFill(get, layerId)), [layerId])
	return useAtomValue(noRoomAtom, { store })
}
